import { CustomerAddress } from '@porterin/booking-flow/src/order-review/domain/entities/CustomerAddress';
import {
  VehicleGoodsType,
  GoodsType,
} from '@porterin/booking-flow/src/order-review/domain/entities/CustomerDetails';
import { ExtendedAddress } from '@porterin/booking-flow/src/order-review/domain/entities/ExtendedAddress';
import {
  GeoRegionRestrictions,
  Restriction,
} from '@porterin/booking-flow/src/order-review/domain/entities/GeoRegionRestrictions';
import { LatLng } from '@porterin/booking-flow/src/order-review/domain/entities/LatLng';
import { ClientData } from '@porterin/booking-flow/src/order-review/domain/entities/OrderReviewClientData';
import { Waypoint } from '@porterin/booking-flow/src/order-review/domain/entities/Waypoint';
import { OrderReviewContentVM } from '@porterin/booking-flow/src/order-review/view-model/order-review-content/OrderReviewContentVM';
import { isCoordsUnderPolygon } from '@porterin/booking-flow/src/order-review/domain/helpers/geoHelper';
import {
  defaultGoodsIndia,
  defaultGoodsInternational,
  defaultGoodsTypeIndia,
  defaultGoodsTypeInternational,
} from '@porterin/booking-flow/src/order-review/view/stores/OrderReviewStore';
import { AppConfig } from '@porterin/component-library/src/ts-utils/analytics/fetch-config/AppConfig';
import { getAppConfig } from '@porterin/component-library/src/ts-utils/analytics/fetch-config/FetchConfig';
import { NextRouter } from 'next/router';

const Config: AppConfig = getAppConfig();

export const getDefaultGoodsType = (
  goods: VehicleGoodsType[],
  vehicle_id: number,
): GoodsType | undefined => {
  const defaultGoodsType = goods.find(
    goodsType => goodsType.vehicleId === vehicle_id,
  );
  return defaultGoodsType
    ? {
        id: defaultGoodsType.id,
        name: defaultGoodsType.name,
      }
    : undefined;
};

export const makeCustomerAddressFromClientData = (
  client_data: ClientData,
  waypoint: Waypoint,
): CustomerAddress => {
  return {
    pickupAddress: {
      landmark: client_data.fromLandmark ?? '',
      location: {
        lat: Number(client_data.fromLat ?? '0.0'),
        lng: Number(client_data.fromLong ?? '0.0'),
      },
      addressDetails: {},
    },
    dropAddress: {
      landmark: client_data.toLandmark ?? '',
      location: {
        lat: Number(client_data.toLat ?? '0.0'),
        lng: Number(client_data.toLong ?? '0.0'),
      },
      addressDetails: {},
    },
    name:
      waypoint === 'Pickup' ? client_data.senderName : client_data.receiverName,
    mobile:
      waypoint === 'Pickup'
        ? client_data.senderMobile
        : client_data.receiverMobile,
    house: waypoint === 'Pickup' ? client_data.fromHouse : client_data.toHouse,
  };
};

export const getAllRestrictedVehiclesFromBounds = (
  location_type: Waypoint,
  lat_lng: LatLng,
  restriction_bounds: GeoRegionRestrictions,
) => {
  let restrictedVehicles: number[] = [];
  restriction_bounds.forEach((restriction: Restriction) => {
    if (
      (location_type == 'Pickup' &&
        (restriction.polygon?.type === 'PickUp' ||
          restriction.polygon?.type === 'Both')) ||
      (location_type == 'Drop' &&
        (restriction.polygon?.type === 'Drop' ||
          restriction.polygon?.type === 'Both'))
    ) {
      if (isCoordsUnderPolygon(restriction.polygon.data, lat_lng)) {
        restrictedVehicles = [...restrictedVehicles, ...restriction.vehicleIds];
      }
    }
  });
  return restrictedVehicles;
};

export const getBookVehicleCtaText = (
  book_cta_text: string,
  vehicle_name: string,
): string => `${book_cta_text} ${vehicle_name}`.trim();

export const getOrderReviewCtaText = (
  router: NextRouter,
  content: OrderReviewContentVM,
  selected_goods_type?: GoodsType,
  selected_vehicle_name?: string,
): string => {
  switch (router.pathname) {
    case '/order-review':
      if (selected_goods_type && selected_vehicle_name) {
        return getBookVehicleCtaText(
          content.goodsType.afterSelectCtaText,
          selected_vehicle_name,
        );
      } else if (selected_goods_type) {
        return content.bookNowCtaText;
      } else {
        return content.goodsType.beforeSelectCtaText;
      }
    default:
      return content.bookNowCtaText;
  }
};

export const formatToIndianCurrency = (amount: number): string => {
  if (Number.isInteger(amount)) {
    return amount.toString();
  }
  return amount.toFixed(2);
};

export const hasAtleastOneAlphabet = (word: string) =>
  /\w*[a-zA-Z]\w*/.test(word);

export const isAddressLandmarkValid = (address: ExtendedAddress): boolean => {
  const hasValidLandmark =
    address.landmark.trim().length > 0 &&
    hasAtleastOneAlphabet(address.landmark);

  return hasValidLandmark;
};

export const isAddressLatLngValid = (address: ExtendedAddress): boolean => {
  const hasValidLocation =
    address.location.lat > 0.0 && address.location.lat < 1000.0;

  return hasValidLocation;
};

export const isMatchingVehicleId = (vehicle_id?: number) => {
  const enableAllVehiclesAgreedDocs =
    Config.enable_all_vehicles_agreed_docs ?? false;
  if (enableAllVehiclesAgreedDocs) {
    return true;
  }
  // Fallback: if the flag is not enabled, return false
  const agreedDocsVehicleIds = Config.agreed_docs_vehicle_ids ?? [];
  if (vehicle_id && agreedDocsVehicleIds.includes(vehicle_id)) {
    return true;
  }
  return false;
};

export const isPfeBkfEnabled = (acc_uuid: string): boolean => {
  const enablePfeBkf = Config.enable_pfe_bkf ?? false;
  const isWhitelistedUuid =
    Config.pfe_bkf_whitelisted_acc_uuids?.includes(acc_uuid) ?? true; // Enable to all uuids if not configured
  return enablePfeBkf && isWhitelistedUuid;
};

// Helper function to get goods list based on country code
export const getDefaultGoodsByCountry = (countryCode: string): GoodsType[] => {
  return countryCode === 'IN' ? defaultGoodsIndia : defaultGoodsInternational;
};

// Helper function to get default goods type based on country code
export const getDefaultGoodsTypeByCountry = (
  countryCode: string,
): GoodsType => {
  return countryCode === 'IN'
    ? defaultGoodsTypeIndia
    : defaultGoodsTypeInternational;
};
